<script setup lang="ts">
import moment from "moment";
import { computed, onUnmounted, ref, watch } from "vue";

import actions from "~/actions";
import EmptyState from "~/components/dumb/EmptyState.vue";
import LoadingSpinner from "~/components/dumb/LoadingSpinner.vue";
import { TASK_CONTEXT_MENU_OPTIONS } from "~/components/visualization/constants";
import { DueDateFieldIcon } from "~/icons";
import { EditorMode, FilterApplicability, FilterConnector } from "~/shared/enums";
import type { FilterGroup, Task } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore, useTenantStore } from "~/stores";
import { makeStringComparator } from "~/utils/comparator";
import { formatDate } from "~/utils/date";
import { getMsUntilNext, getRelativeTimeForDatesDate } from "~/utils/time";
import useLastScrollPosition from "~/utils/useLastScrollPosition";
import usePaginatedList from "~/utils/usePaginatedList";

import HomeTaskRow from "./HomeTaskRow.vue";

const VIEW_DUID = "@upcoming";

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const tenantStore = useTenantStore();

// Refresh mechanism
const now = ref(new Date());
let timeout: ReturnType<typeof setTimeout> | undefined;
const resetValuesAndTimeout = () => {
  now.value = new Date();
  clearTimeout(timeout);
  // eslint-disable-next-line no-restricted-syntax
  timeout = setTimeout(resetValuesAndTimeout, getMsUntilNext("day"));
};
resetValuesAndTimeout();
onUnmounted(() => {
  clearTimeout(timeout);
});

const filterGroup = computed<FilterGroup>(() => ({
  filters: [
    {
      id: "dates",
      propertyDuid: dataStore.defaultDatesProperty.duid,
      applicability: FilterApplicability.IS_BETWEEN,
      connector: FilterConnector.OR,
      values: [[formatDate(now.value), formatDate(moment().add(7, "days"))]],
    },
  ],
  connector: FilterConnector.AND,
}));

const nextWeek = computed(() => moment(now.value).add(7, "days").startOf("hour"));
const upcomingTaskValidator = (task: Task) => {
  const { rolledUpDueAt } = dataStore.getTaskDates(task);
  if (!rolledUpDueAt) {
    return false;
  }

  const today = now.value;
  return !task.inTrash && !task.drafterDuid && moment(rolledUpDueAt).isBetween(today, nextWeek.value, "day", "[]");
};

const tasks = computed(() => dataStore.getLoadedTasksForPageDuid(VIEW_DUID));

const scrollContainer = ref<HTMLElement | null>(null);
const { isLoading, reset } = usePaginatedList(
  computed(() => VIEW_DUID),
  scrollContainer,
  computed(() => dataStore.getLoadedTaskCountForPageDuid(VIEW_DUID)),
  async () =>
    dataStore.loadPaginatedTaskForPageDuid(
      VIEW_DUID,
      filterGroup.value,
      [{ propertyDuid: dataStore.defaultDatesProperty.duid, isAscending: true }],
      upcomingTaskValidator
    ),
  -400
);
useLastScrollPosition(
  computed(() => VIEW_DUID),
  scrollContainer
);

watch([() => filterGroup.value, nextWeek], () => {
  dataStore.resetPaginationStateForPageDuid(VIEW_DUID);
  reset();
});

const upcomingTasks = computed(() => {
  const today = now.value;
  const tasksByDay: Map<string, [string, Task[]]> = new Map();
  tasks.value.forEach((task: Task) => {
    const { rolledUpDueAt } = dataStore.getTaskDates(task);
    if (!rolledUpDueAt) {
      return;
    }

    if (moment(rolledUpDueAt).isBetween(today, nextWeek.value, "day", "[]")) {
      const label = getRelativeTimeForDatesDate(rolledUpDueAt);
      if (!tasksByDay.has(label)) {
        tasksByDay.set(label, [rolledUpDueAt, []]);
      }

      tasksByDay.get(label)?.[1]?.push(task);
    }
  });

  return Array.from(tasksByDay.entries())
    .sort(makeStringComparator((e) => e[1][0]))
    .map(([k, v]) => [k, v[1]] satisfies [string, Task[]]);
});

const onContextMenu = (event: MouseEvent, targetTask: Task) => {
  if (tenantStore.isDart && !pageStore.adminHidden && event.altKey) {
    return;
  }

  event.preventDefault();
  appStore.openContextMenu(
    event as PointerEvent,
    actions.context.task(targetTask, EditorMode.CONTEXT_MENU, [targetTask], false),
    TASK_CONTEXT_MENU_OPTIONS
  );
};
</script>

<template>
  <div class="relative flex h-80 w-full flex-col gap-4 rounded-xl py-4 pl-4 bg-lt">
    <div ref="scrollContainer" class="flex w-full flex-col gap-5 overflow-y-scroll pr-px">
      <div v-for="[label, tasksGroup] in upcomingTasks" :key="label" class="flex flex-col gap-3">
        <span class="select-none truncate text-sm/[18px] text-lt">{{ label }}</span>
        <div class="flex flex-col gap-2">
          <HomeTaskRow
            v-for="task in tasksGroup"
            :key="task.duid"
            :duid="task.duid"
            @context-menu="(event: MouseEvent) => onContextMenu(event, task)" />
        </div>
      </div>
      <template v-if="upcomingTasks.length > 0">
        <LoadingSpinner v-if="isLoading" class="mt-2" />
        <div class="min-h-12 w-full" />
      </template>
    </div>
    <template v-if="upcomingTasks.length === 0">
      <LoadingSpinner v-if="isLoading" absolute />
      <EmptyState v-else :main-icon="DueDateFieldIcon" title="There are no tasks due in the coming week" inline />
    </template>
  </div>
</template>
